/**
 * Utilidades para el formateo de cuantía en la aplicación
 * Unifica todas las funciones de formateo para evitar inconsistencias
 */

/**
 * Función para convertir entrada del usuario (formato europeo estricto) a número
 * Punto = millares, coma = decimales
 * Ej: 1.234,56 => 1234.56
 *     0.50 => 50
 *     0,50 => 0.5
 */
export const parseEuropeanAmount = (value) => {
  if (!value) return 0;
  let str = value.toString().replace(/[^\d.,]/g, '');
  // Quitar puntos (millares)
  str = str.replace(/\./g, '');
  // Reemplazar la coma por punto (decimal)
  str = str.replace(/,/g, '.');
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

/**
 * Función para convertir datos de la API (formato americano) a número
 * Punto = decimal, sin separadores de millares
 * Ej: 100050.55 => 100050.55
 */
export const parseApiAmount = (value) => {
  if (!value) return 0;
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

/**
 * Formatea un número a string europeo: puntos millares, coma decimal, sin decimales si son 00
 * NO muestra ",00" al final, siempre muestra puntos de millares
 */
export const formatNumberToEuropean = (num) => {
  if (typeof num !== 'number' || isNaN(num)) return '';
  
  // Verificar si el número es entero (sin decimales o con .00)
  const isInteger = Number.isInteger(num) || Math.abs(num - Math.round(num)) < 0.001;
  
  if (isInteger) {
    // Para números enteros: mostrar solo el número con puntos de millares
    // Forzar el formato con puntos de millares usando useGrouping: true
    return num.toLocaleString('es-ES', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0,
      useGrouping: true
    });
  } else {
    // Para números con decimales: mostrar con coma decimal
    // Forzar el formato con puntos de millares usando useGrouping: true
    return num.toLocaleString('es-ES', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2,
      useGrouping: true
    });
  }
};

/**
 * Formatea la entrada del usuario a formato europeo visual (NNN.NNN,DD)
 * Siempre muestra puntos de millares, no muestra ",00" al final
 */
export const formatAmountForInput = (value) => {
  const num = parseEuropeanAmount(value);
  return formatNumberToEuropean(num);
};

/**
 * Limpia el valor para enviar a la API (devuelve el número como string con punto decimal)
 */
export const cleanAmountValue = (value) => {
  const num = parseEuropeanAmount(value);
  return num.toString();
};

/**
 * Formatea para mostrar en la UI con símbolo €
 * Usa parseApiAmount para datos de la API (números con punto decimal)
 * NO muestra ",00" al final, siempre muestra puntos de millares
 */
export const formatAmountForDisplay = (value) => {
  const num = parseApiAmount(value);
  if (isNaN(num)) return '';
  return formatNumberToEuropean(num) + ' €';
};

/**
 * Formatea un valor de la API para mostrar en campos de edición
 * Convierte número americano a formato europeo sin símbolo de moneda
 * NO muestra ",00" al final, siempre muestra puntos de millares
 * Ej: 100050.55 => "100.050,55", 100050 => "100.050"
 */
export const formatApiAmountForEdit = (value) => {
  const num = parseApiAmount(value);
  if (isNaN(num)) return '';
  return formatNumberToEuropean(num);
};

export const isValidAmount = (value) => {
  const num = parseEuropeanAmount(value);
  return !isNaN(num) && num >= 0;
};

export const parseAmountToNumber = (value) => {
  return parseEuropeanAmount(value);
}; 